import React, { useState, useRef, useEffect } from 'react';
import { CustomMeal } from '../models/CustomMeal';
import { IconConverter } from '../utils/IconConverter';
import YelpCategories from '../utils/YelpCategories';
import { debug } from '../utils/debug';

const MAX_CATEGORIES = 5;
const MAX_MEAL_NAME_LENGTH = 30;
const MAX_FILTER_LENGTH = 30;

const CreateCustomMeal = ({ onClose, onMealCreated, location }) => {
  const [mealName, setMealName] = useState('');
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [categoryFilter, setCategoryFilter] = useState('');
  const [currentIcon, setCurrentIcon] = useState(() => {
    const icons = IconConverter.getAllMaterialDesignIconNames();
    return icons[Math.floor(Math.random() * icons.length)];
  });
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const popupRef = useRef(null);

  // Load categories on mount
  useEffect(() => {
    // Initialize YelpCategories
    YelpCategories.initializeData();
    
    // Get all categories from YelpCategories
    const categories = YelpCategories.getFlattenedCategories()
      .filter(category => {
        // If category has a blacklist and US is in it, exclude it
        if (category.country_blacklist && category.country_blacklist.includes('US')) {
          return false;
        }
        // If category has a whitelist and US is not in it, exclude it
        if (category.country_whitelist && !category.country_whitelist.includes('US')) {
          return false;
        }
        return true;
      })
      .map(category => ({
        alias: category.alias,
        title: category.title
      }));
    
    setAllCategories(categories);
    setFilteredCategories(categories);
  }, []);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleCategorySelect = (category) => {
    const newSelected = new Set(selectedCategories);
    if (newSelected.has(category.alias)) {
      newSelected.delete(category.alias);
      debug.log('Removed category:', {
        alias: category.alias,
        title: category.title,
        selectedCategories: Array.from(newSelected)
      });
    } else if (newSelected.size < MAX_CATEGORIES) {
      newSelected.add(category.alias);
      debug.log('Added category:', {
        alias: category.alias,
        title: category.title,
        selectedCategories: Array.from(newSelected)
      });
    }
    setSelectedCategories(newSelected);
  };

  const handleCategoryFilter = (filter) => {
    setCategoryFilter(filter);
    if (!filter) {
      setFilteredCategories(allCategories);
    } else {
      const lowerFilter = filter.toLowerCase();
      const filtered = allCategories.filter(cat => 
        cat.title.toLowerCase().includes(lowerFilter) ||
        cat.alias.toLowerCase().includes(lowerFilter)
      );
      setFilteredCategories(filtered);
    }
  };

  const handleMealNameChange = (e) => {
    const value = e.target.value
      .replace(/[:,]/g, '') // Remove colons and commas
      .slice(0, MAX_MEAL_NAME_LENGTH); // Limit to 30 characters
    setMealName(value);
  };

  const handleCreateMeal = () => {
    if (!mealName.trim() || selectedCategories.size === 0) return;

    const mealId = crypto.randomUUID();
    const sfIconName = IconConverter.materialToSf(currentIcon);
    const categoriesString = Array.from(selectedCategories).join(',');

    const customMeal = new CustomMeal(
      `${mealName}:${mealId}:${sfIconName}:${categoriesString}`
    );

    onMealCreated(customMeal);
    onClose();
  };

  const handleReset = () => {
    setSelectedCategories(new Set());
    setCategoryFilter('');
    setFilteredCategories(allCategories);
  };

  const handleRemoveCategory = (category) => {
    setSelectedCategories(prev => {
      const newCategories = prev.filter(c => c !== category);
      debug.log('Removed category:', {
        removed: category,
        remaining: newCategories
      });
      return newCategories;
    });
  };

  const handleAddCategory = (category) => {
    setSelectedCategories(prev => {
      const newCategories = [...prev, category];
      debug.log('Added category:', {
        added: category,
        all: newCategories
      });
      return newCategories;
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div 
        ref={popupRef}
        className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">New Custom Meal</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 flex-1 overflow-y-auto">
          <div className="space-y-4">
            {/* Meal Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Meal Name ({mealName.length}/{MAX_MEAL_NAME_LENGTH})
              </label>
              <input
                type="text"
                value={mealName}
                onChange={handleMealNameChange}
                className="w-full p-2 border border-gray-300 rounded-lg focus:border-accent focus:ring-1 focus:ring-accent"
                placeholder="Enter meal name"
                maxLength={MAX_MEAL_NAME_LENGTH}
              />
            </div>

            {/* Icon Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Icon
              </label>
              <button
                onClick={() => setShowIconPicker(!showIconPicker)}
                className="w-full p-2 border border-gray-300 rounded-lg text-left hover:bg-gray-50 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent flex items-center justify-between"
              >
                <div className="flex items-center">
                  <span className="material-symbols-outlined mr-2">{currentIcon}</span>
                  <span>Select Icon</span>
                </div>
                <span className="material-symbols-outlined text-gray-400">expand_more</span>
              </button>

              {showIconPicker && (
                <div className="mt-2 p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="grid grid-cols-6 gap-2">
                    {IconConverter.getAllMaterialDesignIconNames().map(icon => (
                      <button
                        key={icon}
                        onClick={() => {
                          setCurrentIcon(icon);
                          setShowIconPicker(false);
                        }}
                        className={`p-2 rounded-lg hover:bg-white ${
                          currentIcon === icon ? 'bg-accent text-white' : ''
                        }`}
                      >
                        <span className="material-symbols-outlined">{icon}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Selected Categories Summary */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Selected Categories
              </label>
              <div className="p-2 border border-gray-300 rounded-lg bg-gray-50 min-h-[2.5rem]">
                {selectedCategories.size > 0 ? (
                  <span className="text-gray-700">
                    {Array.from(selectedCategories)
                      .map(alias => allCategories.find(cat => cat.alias === alias)?.title)
                      .filter(Boolean)
                      .join(', ')}
                  </span>
                ) : (
                  <span className="text-gray-400">No categories selected</span>
                )}
              </div>
            </div>

            {/* Categories */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Categories ({selectedCategories.size}/{MAX_CATEGORIES})
                </label>
                <button
                  onClick={handleReset}
                  className="text-sm text-accent hover:text-accent-dark"
                >
                  Reset
                </button>
              </div>
              <input
                type="text"
                value={categoryFilter}
                onChange={(e) => handleCategoryFilter(e.target.value.slice(0, MAX_FILTER_LENGTH))}
                className="w-full p-2 border border-gray-300 rounded-lg focus:border-accent focus:ring-1 focus:ring-accent mb-2"
                placeholder="Filter categories..."
                maxLength={MAX_FILTER_LENGTH}
              />
              <div className="h-48 overflow-y-auto border border-gray-200 rounded-lg">
                {filteredCategories.map(category => (
                  <button
                    key={category.alias}
                    onClick={() => handleCategorySelect(category)}
                    className={`w-full p-2 text-left flex items-center ${
                      selectedCategories.has(category.alias) ? 'bg-accent text-white' : ''
                    }`}
                  >
                    <span className={`material-symbols-outlined mr-2 ${
                      selectedCategories.has(category.alias) ? 'text-white' : 'text-gray-400'
                    }`}>
                      {selectedCategories.has(category.alias) ? 'check_circle' : 'radio_button_unchecked'}
                    </span>
                    {category.title}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleCreateMeal}
            disabled={!mealName.trim() || selectedCategories.size === 0}
            className={`w-full p-3 rounded-lg ${
              mealName.trim() && selectedCategories.size > 0
                ? 'bg-accent text-white hover:bg-accent-dark'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
          >
            Create Meal
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateCustomMeal; 